import { useMemo } from "react";

import { MIN_WIDTH_BREAKPOINTS } from "./media";
import { useViewportWidth } from "./useViewportWidth";
import {
  DEFAULT_MIN_WIDTH_BREAKPOINTS,
  getViewportValues,
  type MinWidthBreakpoints,
  type Viewport,
} from "./viewport-utils";

/**
 * Subscribes to the largest breakpoint at or below the window width rather than
 * the raw width, so resizing within a breakpoint does not re-render consumers.
 * `getViewportValues` gives the same result for the bucket as for the width.
 */
export const useViewport = (
  minWidthBreakpoints: MinWidthBreakpoints = DEFAULT_MIN_WIDTH_BREAKPOINTS
): Viewport => {
  const { tablet, desktop, xlarge } = minWidthBreakpoints;
  const xxlarge = minWidthBreakpoints.xxlarge ?? MIN_WIDTH_BREAKPOINTS.XXLARGE;

  const bucket = useViewportWidth((width) =>
    Math.max(
      0,
      ...[tablet, desktop, xlarge, xxlarge].filter(
        (breakpoint) => breakpoint <= width
      )
    )
  );

  return useMemo(
    () => getViewportValues(bucket, minWidthBreakpoints),
    [bucket, minWidthBreakpoints]
  );
};
